import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';

const BenefitsSection = ({ currentLanguage }) => {
  const content = {
    EN: {
      title: "Key Benefits",
      subtitle: "Everything you need in a prepaid card",
      benefits: [
        {
          icon: "UserCheck",
          title: "No credit check required",
          description: "Get approved regardless of your credit history. Perfect for building or rebuilding credit."
        },
        {
          icon: "RefreshCw",
          title: "Reloadable Mastercard Prepaid Card",
          description: "Add money anytime, anywhere. Use online, in-store, or withdraw cash at ATMs worldwide."
        },
        {
          icon: "ShoppingBag",
          title: "Easy to use for purchases or bill payments",
          description: "Shop online, pay bills, or make everyday purchases with the security of Mastercard."
        },
        {
          icon: "BarChart3",
          title: "Activity may be reported to Equifax",
          description: "Your responsible usage may help build positive credit history over time."
        },
        {
          icon: "Globe",
          title: "Accepted worldwide",
          description: "Use your card anywhere Mastercard is accepted, in Canada and around the world."
        },
        {
          icon: "Smartphone",
          title: "Mobile app management",
          description: "Track spending, reload funds, and manage your account with our easy-to-use mobile app."
        }
      ]
    },
    FR: {
      title: "Avantages clés",
      subtitle: "Tout ce dont vous avez besoin dans une carte prépayée",
      benefits: [
        {
          icon: "UserCheck",
          title: "Aucune vérification de crédit requise",
          description: "Obtenez une approbation quel que soit votre historique de crédit. Parfait pour construire ou reconstruire le crédit."
        },
        {
          icon: "RefreshCw",
          title: "Carte Mastercard prépayée rechargeable",
          description: "Ajoutez de l'argent n'importe quand, n'importe où. Utilisez en ligne, en magasin ou retirez de l'argent aux guichets automatiques dans le monde entier."
        },
        {
          icon: "ShoppingBag",
          title: "Facile à utiliser pour les achats ou le paiement de factures",
          description: "Achetez en ligne, payez des factures ou effectuez des achats quotidiens avec la sécurité de Mastercard."
        },
        {
          icon: "BarChart3",
          title: "L'activité peut être signalée à Equifax",
          description: "Votre utilisation responsable peut aider à construire un historique de crédit positif au fil du temps."
        },
        {
          icon: "Globe",
          title: "Accepté dans le monde entier",
          description: "Utilisez votre carte partout où Mastercard est accepté, au Canada et dans le monde entier."
        },
        {
          icon: "Smartphone",
          title: "Gestion d\'application mobile",
          description: "Suivez les dépenses, rechargez les fonds et gérez votre compte avec notre application mobile facile à utiliser."
        }
      ]
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {text?.title}
          </h2>
          <p className="text-xl text-gray-600">
            {text?.subtitle}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {text?.benefits?.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-2xl p-8 shadow-card hover:shadow-lg transition-shadow duration-300 group"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Icon name={benefit?.icon} size={32} className="text-primary" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {benefit?.title}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {benefit?.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;